import Title from "../components/Title";
import FiltersDrawer from "../components/FiltersDrawer";
import { Filters } from "@/api/Api";
import { mapCoursesToDisplayCourses } from "../lib/mapCoursesToDisplayCourse";
import CoursesCardRectangle from "../components/Cards/CoursesCardRectangle";
import CourseHighlight from "../components/CourseHighlight";
import HorizontalScroll from "../components/HorizontalScroll";
import PillButton from "../components/Buttons/PillButton";
import { useCourseFilters } from "@/hooks/useCourseFilters";
import { useCourses, DisplayCourse } from "@/hooks/useCourses";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import { useActiveCourseFilters } from "@/hooks/useActiveCourseFilters";
import { useRef } from "react";
import { useTrackCourseSelected } from "../hooks/posthog/useTrackCourseSelected";
import { useTrackLevelChange } from "../hooks/posthog/useTrackLevelChange";
import { useTrackTopicsChange } from "../hooks/posthog/useTrackTopicsChange";
import { useTrackCoursePageViewed } from "@/hooks/posthog/useTrackCoursePageViewed";
import { useNavigate } from "react-router-dom";
import { LoadingPage } from "./LoadingPage";

const CoursesPage = () => {
  const { selectedLevel, selectedTopics, setFilters, clearFilters } =
    useActiveCourseFilters();
  const { currentCourseID, setCurrentCourseID } = useCurrentCourse();
  const {
    coursesProgressData,
    currentCourseData,
    isCoursesLoading,
    isCurrentCourseLoading,
  } = useCourses();
  const { topicsData } = useCourseFilters();
  const topics = topicsData?.topics;
  const rectableCourseContainerRef = useRef<HTMLDivElement>(null);
  const trackCourseSelected = useTrackCourseSelected();
  const trackLevelChange = useTrackLevelChange();
  const trackTopicsChange = useTrackTopicsChange();
  const nav = useNavigate();

  // tracks the course page viewed event via posthog
  useTrackCoursePageViewed();

  // Add loading state
  if (isCoursesLoading || isCurrentCourseLoading) {
    return (
      <div className="fixed inset-2 flex items-center justify-center">
        <LoadingPage showText={false} />
      </div>
    );
  }

  let courses: DisplayCourse[] = [];
  if (coursesProgressData?.courses && currentCourseData) {
    courses = mapCoursesToDisplayCourses(
      coursesProgressData,
      currentCourseData
    );
  }

  // Filter courses based on selected level and topics
  const filteredCourses = courses.filter((course: DisplayCourse) => {
    const levelMatch =
      selectedLevel === null ||
      selectedLevel === 0 ||
      course.level.difficulty === selectedLevel;
    const topicMatch =
      selectedTopics.length === 0 ||
      selectedTopics.includes(Number(course.topic));
    return levelMatch && topicMatch;
  });

  const handleSelectCourse = (courseId: number) => {
    setCurrentCourseID(courseId);
    trackCourseSelected();
    setTimeout(() => {
      nav("/home/learn");
    }, 100);
  };

  const handleFilterChange = (filters: Filters) => {
    // here we have to check what filters have changed
    // if the level has changed, we need to track the level change
    // if the topics have changed, we need to track the topics change
    // if both have changed, we need to track both
    // if neither have changed, we don't need to track anything
    if (filters.selectedLevel !== selectedLevel) {
      trackLevelChange(filters.selectedLevel);
    }
    if (filters.selectedTopics !== selectedTopics) {
      trackTopicsChange(filters.selectedTopics);
    }
    setFilters(filters);
  };

  return (
    <div className="flex flex-col justify-start w-full h-full gap-4 pt-14">
      <div className="flex flex-col relative">
        <Title text="Courses" />
        <div className="text-[var(--color-gray)] text-left">
          Pick up where you left off
        </div>
        <div className="absolute right-[1px] top-2">
          <FiltersDrawer
            value={selectedLevel}
            selectedTopics={selectedTopics}
            onFiltersChange={handleFilterChange}
            onClearFilters={clearFilters}
            className="w-[120px]"
          />
        </div>
      </div>
      {filteredCourses?.length > 0 ? (
        <CourseHighlight
          courses={filteredCourses}
          onClick={handleSelectCourse}
        />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-[--large-card-width] p-8 bg-white rounded-xl border border-[var(--color-grey-light)]">
          <div className="text-xl font-semibold text-[var(--color-grey)]">
            No courses found
          </div>
          <div className="text-base font-normal text-[var(--color-grey)]">
            Try adjusting your filters to see more courses
          </div>
        </div>
      )}

      <HorizontalScroll>
        <div className="flex flex-row gap-2 py-1">
          <PillButton
            label="All Topics"
            isSelected={selectedTopics.length === 0}
            onClick={() =>
              handleFilterChange({
                selectedLevel: selectedLevel,
                selectedTopics: [],
              })
            }
          />
          {topics?.map((topic) => (
            <PillButton
              key={topic.id}
              label={topic.name}
              isSelected={selectedTopics.includes(topic.id)}
              onClick={() => {
                if (selectedTopics.includes(topic.id)) {
                  const newTopics = selectedTopics.filter(
                    (id) => id !== topic.id
                  );
                  handleFilterChange({
                    selectedLevel: selectedLevel,
                    selectedTopics: newTopics,
                  });
                } else {
                  handleFilterChange({
                    selectedLevel: selectedLevel,
                    selectedTopics: [...selectedTopics, topic.id],
                  });
                }
              }}
            />
          ))}
        </div>
      </HorizontalScroll>

      <div
        ref={rectableCourseContainerRef}
        className="flex flex-col gap-2 -mt-1 pb-3"
      >
        {filteredCourses
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((course) => (
            <CoursesCardRectangle
              key={course.courseId}
              emoji={course.icon || "📚"}
              selected={course.id === currentCourseID}
              title={course.name}
              subtitle={course.description}
              level={course.level}
              progress={course.progress}
              onClick={() => handleSelectCourse(course.id)}
            />
          ))}
      </div>
    </div>
  );
};

export default CoursesPage;
